import React, { useState } from 'react';
import { IconButton, Tooltip, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Link, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { tokens } from '../../theme';

// Keyboard-focusable explanation of a figure: what it includes and how it is calculated.
// exclamation: circle with «!» instead of «i» (product table and product panel).
//
// Short form: `title` (or children) appears on hover. Long form: when `details` is given, a click
// opens a window with the full plain-language explanation. `details` is a list of paragraphs
// ({ h, t } = heading + text, or a plain string); `sources` lists official Wildberries pages
// ({ label, url }) the explanation relies on. Facts about how MARQIT calculates are written as such;
// facts about Wildberries must come from `sources`.
const InfoTooltip = ({ title, children, size = 16, exclamation = false, details, sources, heading }) => {
  const [open, setOpen] = useState(false);
  const long = Boolean(details && details.length);
  const short = children || title;
  const Icon = exclamation ? ErrorOutlineRoundedIcon : InfoOutlinedIcon;
  return (
    <>
      <Tooltip
        title={<Box sx={{ whiteSpace: 'pre-line' }}>{short}{long ? '\n\nНажмите, чтобы прочитать подробнее.' : ''}</Box>}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
      >
        <IconButton size="small" aria-label="Пояснение к показателю" onClick={long ? () => setOpen(true) : undefined}
          sx={{ p: 0.25, color: tokens.inkMuted, '&:hover': { color: tokens.primaryDark } }}>
          <Icon sx={{ fontSize: size }} />
        </IconButton>
      </Tooltip>
      {long && (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth aria-label="Подробное пояснение">
          <DialogTitle sx={{ pb: 1 }}>{heading || (typeof title === 'string' ? title.split('\n')[0] : 'Пояснение')}</DialogTitle>
          <DialogContent>
            {details.map((d, i) => (
              <Box key={i} sx={{ mb: 1.5 }}>
                {typeof d === 'string' ? <Typography variant="body2">{d}</Typography> : (
                  <>
                    {d.h && <Typography variant="subtitle2" sx={{ mb: 0.25 }}>{d.h}</Typography>}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{d.t}</Typography>
                  </>
                )}
              </Box>
            ))}
            {sources && sources.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${tokens.border}` }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>Официальные источники Wildberries:</Typography>
                {sources.map((s) => (
                  <Typography key={s.url} variant="body2"><Link href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</Link></Typography>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setOpen(false)}>Понятно</Button></DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default InfoTooltip;
